import { ShapeUtils, Vector2 } from 'three';
import { Vec3, sub, cross, normalize, dot, add, mul, len, EPS, closestPointOnSegment, dist } from './vec';
import { FaceData, Model } from './model';

/** Geometrie-Hilfsfunktionen für planare Flächen. */

/** Flächennormale über Newell-Verfahren (robust bei konkaven Ringen). */
export function faceNormal(points: Vec3[]): Vec3 {
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    nx += (a.y - b.y) * (a.z + b.z);
    ny += (a.z - b.z) * (a.x + b.x);
    nz += (a.x - b.x) * (a.y + b.y);
  }
  return normalize({ x: nx, y: ny, z: nz });
}

export interface PlaneBasis {
  origin: Vec3;
  /** orthonormal: u × v = n */
  u: Vec3;
  v: Vec3;
  n: Vec3;
}

/** Orthonormales Koordinatensystem in der Flächenebene (u entlang der längsten ersten Kante). */
export function faceBasis(points: Vec3[]): PlaneBasis {
  const n = faceNormal(points);
  // längste Kante suchen für stabile u-Achse
  let uDir: Vec3 = { x: 1, y: 0, z: 0 };
  let best = -1;
  for (let i = 0; i < points.length; i++) {
    const d = sub(points[(i + 1) % points.length]!, points[i]!);
    const l = len(d);
    if (l > best) { best = l; uDir = d; }
  }
  const u = best > EPS ? normalize(uDir) : { x: 1, y: 0, z: 0 };
  const v = cross(n, u);
  return { origin: points[0]!, u, v, n };
}

export function toBasis2D(basis: PlaneBasis, p: Vec3): { x: number; y: number } {
  const d = sub(p, basis.origin);
  return { x: dot(d, basis.u), y: dot(d, basis.v) };
}

export function fromBasis2D(basis: PlaneBasis, x: number, y: number): Vec3 {
  return add(basis.origin, add(mul(basis.u, x), mul(basis.v, y)));
}

/** Punkt-in-Polygon-Test (2D, Ray-Casting). */
export function pointInPolygon(x: number, y: number, poly: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]!.x, yi = poly[i]!.y;
    const xj = poly[j]!.x, yj = poly[j]!.y;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export interface TriangulatedFace {
  /** Positionen der Dreiecksecken, 9 Zahlen pro Dreieck. */
  triangles: Vec3[];
}

/**
 * Trianguliert eine Fläche mit optionalen Löchern (Ear-Clipping über THREE.ShapeUtils).
 */
export function triangulateFace(outer: Vec3[], holes: Vec3[][]): TriangulatedFace {
  const triangles: Vec3[] = [];
  if (outer.length < 3) return { triangles };
  const basis = faceBasis(outer);
  const toVec2 = (p: Vec3): Vector2 => {
    const d = toBasis2D(basis, p);
    return new Vector2(d.x, d.y);
  };
  const outerV2 = outer.map(toVec2);
  const holesV2 = holes.map((h) => h.map(toVec2));
  const allPoints: Vector2[] = [...outerV2, ...holesV2.flat()];
  const indexTriangles = ShapeUtils.triangulateShape(outerV2, holesV2);
  for (const t of indexTriangles) {
    for (const idx of t) {
      const p2 = allPoints[idx];
      if (p2) triangles.push(fromBasis2D(basis, p2.x, p2.y));
    }
  }
  return { triangles };
}

/** Flächenschwerpunkt des Rings (Mittelwert der Eckpunkte — gut genug für Tests/Anzeige). */
export function loopCentroid(points: Vec3[]): Vec3 {
  let x = 0, y = 0, z = 0;
  for (const p of points) { x += p.x; y += p.y; z += p.z; }
  return { x: x / points.length, y: y / points.length, z: z / points.length };
}

/** Nächster Punkt auf dem Ring-Umfang (Außenring). */
export function closestPointOnLoop(p: Vec3, points: Vec3[]): { point: Vec3; distance: number; segment: number } {
  let best = { point: points[0]!, distance: Infinity, segment: -1 };
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    const hit = closestPointOnSegment(p, a, b);
    if (hit.distance < best.distance) best = { point: hit.point, distance: hit.distance, segment: i };
  }
  return best;
}

/** Ringumfang (Außenring). */
export function loopPerimeter(points: Vec3[]): number {
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    total += dist(points[i]!, points[(i + 1) % points.length]!);
  }
  return total;
}

/**
 * Versetzt einen 2D-Ring um `distance` nach innen (Winkelhalbierenden-Methode).
 * Ohne Selbstschnitt-Bereinigung — MVP-Einschränkung.
 */
export function offsetLoop2D(poly: { x: number; y: number }[], distance: number): { x: number; y: number }[] {
  const n = poly.length;
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const prev = poly[(i - 1 + n) % n]!;
    const cur = poly[i]!;
    const next = poly[(i + 1) % n]!;
    const d1 = normalize2(sub2(cur, prev));
    const d2 = normalize2(sub2(next, cur));
    // Innenwinkel-Halbierende: für gegen den Uhrzeigersinn gewundene Ringe zeigt (-d1+d2)... 
    const bis = normalize2({ x: d1.x + d2.x, y: d1.y + d2.y });
    const sinHalf = Math.sqrt(Math.max(0, (1 - (d1.x * d2.x + d1.y * d2.y)) / 2));
    const scale = sinHalf > 1e-9 ? distance / sinHalf : distance;
    // Normale der Halbierenden nach innen ( links der Wegrichtung bei CCW)
    const inward = { x: -bis.y, y: bis.x };
    out.push({ x: cur.x + inward.x * scale, y: cur.y + inward.y * scale });
  }
  return out;
}

function sub2(a: { x: number; y: number }, b: { x: number; y: number }) {
  return { x: a.x - b.x, y: a.y - b.y };
}

function normalize2(a: { x: number; y: number }) {
  const l = Math.hypot(a.x, a.y) || 1;
  return { x: a.x / l, y: a.y / l };
}

/** Liest die Positionen eines Flächenrings (Außenring) aus dem Modell. */
export function faceLoopPoints(model: Model, f: FaceData): Vec3[] {
  return f.outer.map((id) => model.vertexPosition(id));
}
